import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@dearbill/shared";
import { useOrg } from "./OrgContext";

// The tab class keeps `min-h-11`, so every tab is a full-size touch target.
const TAB_CLASS = ({ isActive }: { isActive: boolean }): string =>
  cn(
    "inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-[3px] px-4 pb-[13px] pt-4 text-[17px] leading-6 transition-colors",
    isActive
      ? "border-ember text-ember font-semibold"
      : "text-foreground hover:text-ember border-transparent font-medium",
  );

/**
 * Tab bar for the org page: Home (`home`), Member cost share (`cost-tool`),
 * Referrals (`forms`), Team (`members`) and Organization (`organization`).
 *
 * Gating:
 *  - Home, Member cost share and Referrals are navigable for every org. The server
 *    authorizes a pending org's cost-tool and enrollments-list reads on
 *    membership alone, and gates referral minting on `can_take_referrals`, so an
 *    incomplete org reaching these tabs sees empty state, not an error.
 *  - Team shows only to an `org_admin`; `RequireOrgAdmin` also guards its route.
 *  - The "info required" icon stays on Organization until the org profile is
 *    complete.
 *
 * The bar has no referral or help controls. Home's "Refer a patient" card and
 * the Referrals tab's button open `ReferSomeoneModal`, and the Member cost share page
 * carries its own `HelpWalkthrough`.
 */
export function OrgTabBar(): ReactNode {
  const { orgId, res, isOrgAdmin } = useOrg();

  // An incomplete org profile flags the Organization tab; it no longer locks any
  // other tab.
  const orgIncomplete = !res.complete;

  return (
    <div className="border-haze sticky top-0 z-10 border-b bg-white">
      <nav
        aria-label="Organization"
        className="max-w-page mx-auto flex w-full min-w-0 items-stretch gap-1.5 overflow-x-auto px-7"
      >
        <NavLink to={`/orgs/${orgId}/home`} className={TAB_CLASS}>
          Home
        </NavLink>

        <NavLink to={`/orgs/${orgId}/cost-tool`} className={TAB_CLASS}>
          Member cost share
        </NavLink>

        <NavLink
          to={`/orgs/${orgId}/forms`}
          className={TAB_CLASS}
          data-tour="pharmacy-referrals-tab"
        >
          Referrals
        </NavLink>

        {isOrgAdmin && (
          <NavLink to={`/orgs/${orgId}/members`} className={TAB_CLASS}>
            Team
          </NavLink>
        )}

        <NavLink to={`/orgs/${orgId}/organization`} className={TAB_CLASS}>
          Organization
          {orgIncomplete && <OrgInfoRequiredIcon />}
        </NavLink>
      </nav>
    </div>
  );
}

/** Warning icon beside the Organization tab while the org profile is incomplete. */
function OrgInfoRequiredIcon(): ReactNode {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-warning shrink-0"
      role="img"
      aria-label="Organization info required"
    >
      <title>Organization info required</title>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
